import merge from 'lodash.merge';
import {post} from '../utils/ajaxHelper';
import {log as logToServer} from '../api/logger';
import {getGeneratedLocale} from '../utils/localeUtils';

function handleGraphQlError(err, operationName, options) {
    const errors = (err && err.response && err.response.body && err.response.body.errors) || [];
    let message = 'An error occurred while processing your graphql request.';
    errors.forEach((currentError) => {
        logToServer({
            graphQlService: operationName,
            graphqlerror: true,
            message: currentError.message
        });
        message = currentError.message;
    });

    if (!errors.length) {
        logToServer({
            nodeService: `${operationName} exception`,
            graphqlerror: true,
            error: err, // hits up any serialization.
            options: JSON.stringify(options)
        });
    }

    // transform all the errors into something basic.
    const errorCode = (err.response && err.response.statusCode) || 500;
    return {
        errorCode,
        originalErrorCode: errorCode, // backwards compatability
        message,
        errors
    };
}

function mutateGraphQlResponse(response, operationName) { // eslint-disable-line complexity
    if (response.errorCode) {
        return response;
    }

    const graphqlRequestMarker = response.headers && response.headers['x-homeaway-requestmarker'] || null;
    const data = response.body || {data: {}};

    if (data.errors && data.errors.length) {
        data.errors.forEach((currentError) => {
            logToServer({
                graphQlService: operationName,
                graphqlerror: true,
                message: currentError.message,
                graphqlRequestMarker
            });
        });

        const errorBlock = data.errors.find((dataError) => {
            // We only care about an error if it has as statusCode in it.
            return !!(dataError.extensions && dataError.extensions.statusCode);
        });

        if (errorBlock) {
            if (graphqlRequestMarker) {
                errorBlock.extensions.graphqlRequestMarker = graphqlRequestMarker;
            }

            return errorBlock.extensions;
        }
    }

    data.data.endTime = Date.now();

    if (graphqlRequestMarker) {
        data.data.graphqlRequestMarker = graphqlRequestMarker;
    }

    return data.data;
}

/**
 * Handles POST to GraphQl Endpoint
 *
 * @param {*} query
 * @param {*} variables
 */
async function graphqlQuery(operation, query, variables, locale, waitForAccessToken) { // eslint-disable-line complexity
    let options = {
        query,
        variables,
        operation
    };

    // We are fighting bots, here.
    // We need a client side value that can only be generated if js is ran.
    // In this case, liveLab is used to wire up a generator function.
    // When we make the pricing call, use it to get the value, and pass it through.
    // It is then compared in the csrfHelper on the server, and compared against
    // the value found in the ha_pdp_bingoCard throttle.
    if (typeof window !== 'undefined' && window.bingoCage && window.bingoCage.turn) {
        const livelabBingoValue = window.bingoCage.turn();
        let bingoObject = {};
        try {
            bingoObject = JSON.parse(livelabBingoValue);
        } catch (err) {
            // do nothing;
        }

        if (typeof bingoObject === 'object' && Object.keys(bingoObject).length) {
            options = merge({}, options, bingoObject);
        } else if (livelabBingoValue) {
            options.systemTime = livelabBingoValue
        }
    }

    const headers = {};

    if (locale) {
        headers['X-HomeAway-DisplayLocale'] = getGeneratedLocale(locale);
    }

    const url = '/pdp/graphql';
    const result = await post(url, options, headers, waitForAccessToken).catch((err) => {
        return handleGraphQlError(err, operation, variables);
    });

    // handleGraphQlError will add an errorCode, and already setup what we
    // want to return.
    if (!result.errorCode) {
        return mutateGraphQlResponse(result, operation);
    }

    return result;
}

export default graphqlQuery;
export {
    graphqlQuery,
    handleGraphQlError,
    mutateGraphQlResponse};
